import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  ShieldAlert,
  Search,
  Info,
  User,
  Tag,
  ReceiptText,
  AlertTriangle,
} from 'lucide-react';
import apiClient from '../../api/client';

const formatearFecha = (fecha) => {
  if (!fecha) return '';
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const d = new Date(fecha);
  if (isNaN(d)) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = meses[d.getMonth()];
  const anio = d.getFullYear();
  return `${dia}-${mes}-${anio}`;
};

const obtenerFechaLocal = (fecha) => {
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

const calcularDiasDesdeFecha = (fecha) => {
  const [anio, mes, dia] = fecha.split('-').map(Number);
  const fechaSeleccionada = Date.UTC(anio, mes - 1, dia);
  const hoy = new Date();
  const fechaHoy = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.max(1, Math.floor((fechaHoy - fechaSeleccionada) / 86400000) + 1);
};

/**
 * Modal para marcar un cruce como ABUSO directamente desde el módulo de abusos.
 *
 * Flujo:
 * 1. Se solicita el dispositivo (TAG) y se buscan sus cruces recientes.
 * 2. Al seleccionar un cruce se muestra el responsable del TAG (Control_Tags);
 *    si la matrícula del cruce no coincide, permite seleccionar la matrícula del
 *    operador que cometió el abuso y corrige el registro en la tabla de cruces
 *    al momento de marcarlo como Abuso.
 */
const MarcarAbusoModal = ({ show, onClose, onMarcado }) => {
  const [paso, setPaso] = useState(1);

  // Paso 1: búsqueda por dispositivo
  const [tagBuscado, setTagBuscado] = useState('');
  const [dias, setDias] = useState(90);
  const [fechaEspecifica, setFechaEspecifica] = useState('');
  const [fechaBuscada, setFechaBuscada] = useState('');
  const [filtroCaseta, setFiltroCaseta] = useState('');
  const [costoMin, setCostoMin] = useState('');
  const [costoMax, setCostoMax] = useState('');
  const [cruces, setCruces] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [cruceSeleccionado, setCruceSeleccionado] = useState(null);

  // Paso 2: responsable del TAG y corrección de matrícula
  const [responsableTag, setResponsableTag] = useState(null);
  const [loadingPaso2, setLoadingPaso2] = useState(false);
  const [corregirMatricula, setCorregirMatricula] = useState(false);
  const [operadores, setOperadores] = useState([]);
  const [operadoresExtra, setOperadoresExtra] = useState([]); // Matrículas buscadas en el histórico (incluye bajas)
  const [buscandoMatricula, setBuscandoMatricula] = useState(false);
  const [filtroOperador, setFiltroOperador] = useState('');
  const [operadorSeleccionado, setOperadorSeleccionado] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState('Fecha');
  const [ordenAsc, setOrdenAsc] = useState(false);

  /* El usuario puede teclear solo el número del dispositivo; los TAGs se almacenan con el prefijo IMDM. */
  const normalizarTag = () => {
    const t = tagBuscado.trim().toUpperCase();
    return /^\d+$/.test(t) ? `IMDM${t}` : t;
  };

  const matriculaDelCruce = cruceSeleccionado?.No_Economico?.split(' ')[0] || '';
  const matriculaResponsable = responsableTag ? String(responsableTag.id_matricula) : '';
  const matriculasCoinciden = !responsableTag || String(matriculaDelCruce) === matriculaResponsable;

  /* Filtros locales para acotar los cruces encontrados: por caseta y por rango de costo del cruce. */
  const crucesFiltrados = cruces.filter(c => {
    const casetaOK = !filtroCaseta.trim() || c.Caseta?.toLowerCase().includes(filtroCaseta.trim().toLowerCase());
    const min = parseFloat(costoMin);
    const max = parseFloat(costoMax);
    const costoMinOK = isNaN(min) || c.Importe >= min;
    const costoMaxOK = isNaN(max) || c.Importe <= max;
    return casetaOK && costoMinOK && costoMaxOK;
  });

  /* Si el cruce seleccionado queda fuera de los filtros, se deselecciona. */
  useEffect(() => {
    if (cruceSeleccionado && !crucesFiltrados.some(c => c.ID === cruceSeleccionado.ID)) {
      setCruceSeleccionado(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroCaseta, costoMin, costoMax]);

  const limpiarEstado = () => {
    setPaso(1);
    setTagBuscado('');
    setDias(90);
    setFechaEspecifica('');
    setFechaBuscada('');
    setFiltroCaseta('');
    setCostoMin('');
    setCostoMax('');
    setCruces([]);
    setBuscando(false);
    setBusquedaRealizada(false);
    setCruceSeleccionado(null);
    setResponsableTag(null);
    setCorregirMatricula(false);
    setOperadoresExtra([]);
    setBuscandoMatricula(false);
    setFiltroOperador('');
    setOperadorSeleccionado('');
    setGuardando(false);
  };

  const cerrar = () => {
    limpiarEstado();
    onClose();
  };

  const buscarCruces = async (e) => {
    e?.preventDefault();
    const tag = normalizarTag();
    if (tag.length < 4) {
      alert('Introduce el número del dispositivo (TAG) para buscar sus cruces.');
      return;
    }
    try {
      setBuscando(true);
      setCruceSeleccionado(null);
      const fechaSeleccionada = fechaEspecifica;
      const diasBusqueda = fechaSeleccionada ? calcularDiasDesdeFecha(fechaSeleccionada) : dias;
      const { data } = await apiClient.get(`/api/cruces/tag/${tag}?dias=${diasBusqueda}`);
      const resultados = data || [];
      setCruces(fechaSeleccionada
        ? resultados.filter(cruce => obtenerFechaLocal(cruce.Fecha) === fechaSeleccionada)
        : resultados);
      setFechaBuscada(fechaSeleccionada);
      setBusquedaRealizada(true);
    } catch (err) {
      console.error('Error al buscar cruces por TAG:', err);
      alert('❌ Error al buscar los cruces del dispositivo.');
    } finally {
      setBuscando(false);
    }
  };

  /* Al continuar al paso 2 se consulta el responsable actual del TAG y el catálogo de operadores. */
  const continuarPaso2 = async () => {
    if (!cruceSeleccionado) return;
    setPaso(2);
    setLoadingPaso2(true);
    try {
      const tag = normalizarTag();
      const [tagsRes, operadoresRes] = await Promise.all([
        apiClient.get(`/api/tags`),
        apiClient.get(`/api/tracking/operadores`),
      ]);
      const responsable = (tagsRes.data || []).find(t => t.Dispositivo === tag) || null;
      setResponsableTag(responsable);
      setOperadores(operadoresRes.data || []);

      // Si la matrícula del cruce no coincide con el responsable del TAG, se sugiere la corrección.
      const matCruce = cruceSeleccionado?.No_Economico?.split(' ')[0] || '';
      if (responsable && String(responsable.id_matricula) !== String(matCruce)) {
        setCorregirMatricula(true);
        setOperadorSeleccionado(String(responsable.id_matricula));
      }
    } catch (err) {
      console.error('Error al consultar el responsable del TAG:', err);
      alert('❌ Error al consultar el responsable del TAG.');
    } finally {
      setLoadingPaso2(false);
    }
  };

  /* El responsable del TAG podría ya no aparecer en el catálogo de operadores activos (baja, cambio de puesto);
     se agrega como opción para poder asignarle el abuso de todas formas. También se agregan las matrículas
     buscadas manualmente en el histórico del personal (operadoresExtra). */
  const listaOperadoresCompleta = (() => {
    const lista = [...operadores];
    for (const extra of operadoresExtra) {
      if (!lista.some(op => String(op.id) === String(extra.id))) {
        lista.unshift(extra);
      }
    }
    if (responsableTag && !lista.some(op => String(op.id) === String(responsableTag.id_matricula))) {
      lista.unshift({
        id: responsableTag.id_matricula,
        nombre: `${responsableTag.Nombres || ''} ${responsableTag.Ap_paterno || ''} ${responsableTag.Ap_materno || ''}`.trim(),
      });
    }
    return lista;
  })();

  const opcionesOperadores = (() => {
    if (!filtroOperador.trim()) return listaOperadoresCompleta;
    const f = filtroOperador.trim().toLowerCase();
    return listaOperadoresCompleta.filter(op => String(op.id).includes(f) || op.nombre.toLowerCase().includes(f));
  })();

  /* La matrícula tecleada no está entre los operadores conocidos y es numérica:
     se ofrece buscarla en todo el personal (incluye bajas). */
  const matriculaFiltro = filtroOperador.trim();
  const ofrecerBusquedaHistorico = corregirMatricula
    && /^\d+$/.test(matriculaFiltro)
    && !listaOperadoresCompleta.some(op => String(op.id) === matriculaFiltro);

  const buscarMatriculaHistorico = async () => {
    try {
      setBuscandoMatricula(true);
      const { data } = await apiClient.get(`/api/tracking/operadores/${matriculaFiltro}`);
      setOperadoresExtra(prev => prev.some(o => String(o.id) === String(data.id)) ? prev : [...prev, data]);
      setOperadorSeleccionado(String(data.id));
      setFiltroOperador('');
    } catch (err) {
      if (err.response?.status === 404) {
        alert(`No se encontró personal con la matrícula ${matriculaFiltro}.`);
      } else {
        console.error('Error al buscar la matrícula en el histórico:', err);
        alert('❌ Error al buscar la matrícula en el histórico del personal.');
      }
    } finally {
      setBuscandoMatricula(false);
    }
  };

  const manejarOrdenar = (campo) => {
    if (ordenarPor === campo) {
      setOrdenAsc((prev) => !prev);
    } else {
      setOrdenarPor(campo);
      setOrdenAsc(true);
    }
  };

  const obtenerValorOrden = (cruce, campo) => {
    switch (campo) {
      case 'Fecha': {
        const fecha = new Date(cruce.Fecha || 0);
        return Number.isNaN(fecha.getTime()) ? 0 : fecha.getTime();
      }
      case 'Caseta':
        return cruce.Caseta || '';
      case 'No_Economico':
        return cruce.No_Economico || '';
      case 'Clase':
        return cruce.Clase || '';
      case 'Importe':
        return Number(cruce.Importe) || 0;
      case 'Estatus':
        return cruce.Estatus || cruce.Dictamen || '';
      default:
        return '';
    }
  };

  const crucesOrdenados = [...crucesFiltrados].sort((a, b) => {
    const valorA = obtenerValorOrden(a, ordenarPor);
    const valorB = obtenerValorOrden(b, ordenarPor);

    if (typeof valorA === 'number' && typeof valorB === 'number') {
      return ordenAsc ? valorA - valorB : valorB - valorA;
    }

    const comparacion = String(valorA).localeCompare(String(valorB), 'es', { sensitivity: 'base' });
    return ordenAsc ? comparacion : -comparacion;
  });

  const marcarComoAbuso = async () => {
    if (!cruceSeleccionado) return;
    try {
      setGuardando(true);
      if (corregirMatricula) {
        const op = listaOperadoresCompleta.find(o => String(o.id) === String(operadorSeleccionado));
        if (!op) {
          alert('Selecciona la matrícula del operador que cometió el abuso.');
          setGuardando(false);
          return;
        }
        // Se corrige la matrícula y se dictamina como Abuso en la tabla de cruces.
        // Cruces.No_Economico es varchar(15): se recorta igual que lo hace el importador.
        await apiClient.patch(`/api/cruces/${cruceSeleccionado.ID}/editar`, {
          DatosAGuardar: {
            caseta: cruceSeleccionado.Caseta,
            No_Economico: `${op.id} ${op.nombre}`.substring(0, 15),
            id_orden: cruceSeleccionado.id_orden,
            Dictamen: 'Abuso',
            Importe: cruceSeleccionado.Importe,
          },
        });
      } else {
        await apiClient.patch(`/api/cruces/${cruceSeleccionado.ID}/estatus`, { estatus: 'Abuso' });
      }
      alert('✅ El cruce fue marcado como ABUSO correctamente.');
      limpiarEstado();
      if (onMarcado) onMarcado();
    } catch (err) {
      console.error('Error al marcar el cruce como abuso:', err);
      alert('❌ Error al marcar el cruce como abuso.');
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={cerrar} size="xl" animation={true}>
      <Modal.Header style={{ paddingBottom: '0.5rem' }}>
        <Modal.Title>
          <span style={{ float: 'right !important', right: '10px', position: 'absolute', fontSize: '.7rem' }}>
            {cruceSeleccionado ? `ID: ${cruceSeleccionado.ID}` : ''}
          </span>
          <ShieldAlert size={35} className="me-1 text-danger" /> Marcar cruce como abuso
          {busquedaRealizada && (
            <>
              <br></br>
              <a href={`https://apps.pase.com.mx/uc/detalletag/IMDM/${normalizarTag().replace("IMDM", "")}`} style={{ fontSize: '.9rem', }} target='_blank' rel="noreferrer">
                <ReceiptText size={23} className="me-1 ml-2 mr-3" />
                TAG: {normalizarTag()}
              </a>
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ paddingTop: '0.5rem' }}>

        {/* ================== PASO 1: Búsqueda del cruce por dispositivo ================== */}
        {paso === 1 && (
          <div className="card" style={{ background: 'whitesmoke', }}>
            <div className='card-header text-center'>
              <Info size={18} className="me-1" />
              <span className='text-primary font-weight-bolder ml-2'>Busca el dispositivo para localizar el cruce</span>
            </div>
            <div className="card-body" style={{ background: 'whitesmoke', }}>
              <form onSubmit={buscarCruces} className="d-flex flex-wrap align-items-end mb-3">
                <div className="form-floating pr-2">
                  <input
                    type="text"
                    id="inDispositivoAbuso"
                    className="form-control form-control-sm"
                    placeholder="Dispositivo / TAG"
                    autoComplete="off"
                    autoFocus
                    value={tagBuscado}
                    onChange={(e) => setTagBuscado(e.target.value)}
                  />
                  <label htmlFor="inDispositivoAbuso">Dispositivo / TAG</label>
                </div>

                <div className="pl-1 pr-2 input-group-text mb-0">
                  <label className='form-label mb-0' htmlFor='inDiasAbuso'>Buscar en:</label>
                  <select className='mx-3 p-2' id='inDiasAbuso' value={dias} disabled={Boolean(fechaEspecifica)} onChange={(e) => setDias(parseInt(e.target.value))}>
                    <option value={30}>Últimos 30 días</option>
                    <option value={90}>Últimos 90 días</option>
                    <option value={180}>Últimos 180 días</option>
                    <option value={365}>Último año</option>
                    <option value={730}>Últimos 2 años</option>
                    <option value={1825}>Últimos 5 años</option>
                    <option value={3650}>Todo el histórico</option>
                  </select>
                </div>

                <div className="form-floating pr-2" style={{ maxWidth: '11rem' }}>
                  <input
                    type="date"
                    id="inFechaEspecificaAbuso"
                    className="form-control form-control-sm"
                    max={obtenerFechaLocal(new Date())}
                    value={fechaEspecifica}
                    onChange={(e) => setFechaEspecifica(e.target.value)}
                  />
                  <label htmlFor="inFechaEspecificaAbuso">Fecha específica</label>
                </div>

                <div className="form-floating pr-2" style={{ maxWidth: '11rem' }}>
                  <input
                    type="text"
                    id="inFiltroCaseta"
                    className="form-control form-control-sm"
                    placeholder="Caseta"
                    autoComplete="off"
                    value={filtroCaseta}
                    onChange={(e) => setFiltroCaseta(e.target.value)}
                  />
                  <label htmlFor="inFiltroCaseta">Caseta</label>
                </div>

                <div className="form-floating pr-2" style={{ maxWidth: '8rem' }}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    id="inCostoMin"
                    className="form-control form-control-sm"
                    placeholder="$ Desde"
                    value={costoMin}
                    onChange={(e) => setCostoMin(e.target.value)}
                  />
                  <label htmlFor="inCostoMin">Costo desde $</label>
                </div>

                <div className="form-floating pr-2" style={{ maxWidth: '8rem' }}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    id="inCostoMax"
                    className="form-control form-control-sm"
                    placeholder="$ Hasta"
                    value={costoMax}
                    onChange={(e) => setCostoMax(e.target.value)}
                  />
                  <label htmlFor="inCostoMax">Costo hasta $</label>
                </div>

                <button type="submit" className="btn btn-primary ml-2" disabled={buscando}>
                  <Search size={18} className="me-1 mr-1" />
                  {buscando ? 'Buscando...' : 'Buscar cruces'}
                </button>
              </form>

              {buscando && (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "10vh" }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                    <span className="visually-hidden">.</span>
                  </div>
                </div>
              )}

              {!buscando && busquedaRealizada && cruces.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  <AlertTriangle size={18} className="me-1 mr-2" />
                  No se encontraron cruces del dispositivo <b>{normalizarTag()}</b>
                  {fechaBuscada
                    ? <> el <b>{formatearFecha(`${fechaBuscada}T00:00:00`)}</b>.</>
                    : ' en el periodo seleccionado.'}
                </div>
              )}

              {!buscando && cruces.length > 0 && crucesFiltrados.length === 0 && (
                <div className="alert alert-info mb-0" role="alert">
                  <Info size={18} className="me-1 mr-2" />
                  Ningún cruce coincide con los filtros de caseta y costo; ajústalos para ver los {cruces.length} cruces encontrados.
                </div>
              )}

              {!buscando && crucesFiltrados.length > 0 && (
                <>
                  <span className='text-muted'>Selecciona el cruce que se determinó como abuso ({crucesFiltrados.length} de {cruces.length} encontrados):</span>
                  <div className="table-responsive table-container mt-2" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                    <table className="table table-bordered table-sm table-hover align-middle">
                      <thead className="text-center table-dark">
                        <tr>
                          <th></th>
                          <th>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-white text-decoration-none border-0"
                              onClick={() => manejarOrdenar('Fecha')}
                              style={{ cursor: 'pointer' }}
                            >
                              Fecha/Hora {ordenarPor === 'Fecha' ? (ordenAsc ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-white text-decoration-none border-0"
                              onClick={() => manejarOrdenar('Caseta')}
                              style={{ cursor: 'pointer' }}
                            >
                              Caseta {ordenarPor === 'Caseta' ? (ordenAsc ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-white text-decoration-none border-0"
                              onClick={() => manejarOrdenar('No_Economico')}
                              style={{ cursor: 'pointer' }}
                            >
                              Matricula/Operador {ordenarPor === 'No_Economico' ? (ordenAsc ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-white text-decoration-none border-0"
                              onClick={() => manejarOrdenar('Clase')}
                              style={{ cursor: 'pointer' }}
                            >
                              Clase {ordenarPor === 'Clase' ? (ordenAsc ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-white text-decoration-none border-0"
                              onClick={() => manejarOrdenar('Importe')}
                              style={{ cursor: 'pointer' }}
                            >
                              Importe {ordenarPor === 'Importe' ? (ordenAsc ? '↑' : '↓') : ''}
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-white text-decoration-none border-0"
                              onClick={() => manejarOrdenar('Estatus')}
                              style={{ cursor: 'pointer' }}
                            >
                              Dictamen {ordenarPor === 'Estatus' ? (ordenAsc ? '↑' : '↓') : ''}
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {crucesOrdenados.map((cruce) => {
                          const yaEsAbuso = cruce.Estatus === 'Abuso';
                          return (
                            <tr
                              key={cruce.ID}
                              onClick={() => !yaEsAbuso && setCruceSeleccionado(cruce)}
                              className={(cruceSeleccionado?.ID === cruce.ID) ? 'table-danger font-weight-bold' : (yaEsAbuso ? 'text-muted' : '')}
                              style={{ cursor: yaEsAbuso ? 'not-allowed' : 'pointer' }}
                            >
                              <td className='text-center align-middle'>
                                <input
                                  type="radio"
                                  name="cruceAbuso"
                                  disabled={yaEsAbuso}
                                  checked={cruceSeleccionado?.ID === cruce.ID}
                                  onChange={() => setCruceSeleccionado(cruce)}
                                />
                              </td>
                              <td className='text-center'>
                                {formatearFecha(cruce.Fecha)}
                                <br />
                                <small className="text-muted">{cruce.Fecha?.split('T')[1]?.split('.')[0]}</small>
                              </td>
                              <td>{cruce.Caseta}</td>
                              <td>{cruce.No_Economico}</td>
                              <td className='text-center'>{cruce.Clase}</td>
                              <td className='text-center'>${cruce.Importe?.toFixed(2)}</td>
                              <td className='text-center'>
                                {yaEsAbuso
                                  ? <span className="badge badge-danger">Ya es abuso</span>
                                  : (cruce.Estatus || 'Pendiente')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ================== PASO 2: Responsable del TAG y corrección de matrícula ================== */}
        {paso === 2 && (
          <>
            {loadingPaso2 ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "20vh" }}>
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">.</span>
                </div>
              </div>
            ) : (
              <>
                <div className="card" style={{ background: 'whitesmoke', }}>
                  <div className='card-header text-center'>
                    <Info size={18} className="me-1" />
                    <span className='text-primary font-weight-bolder ml-2'>Información del cruce a determinar como <b>ABUSO</b></span>
                  </div>
                  <div className="card-body" style={{ background: 'whitesmoke', }}>
                    <div className='form-row align-items-center'>
                      <div className="form-floating pr-2 col">
                        Fecha:
                        <span className='ml-1 text-secondary font-weight-bolder'>
                          {formatearFecha(cruceSeleccionado?.Fecha)} <span className='font-weight-bolder text-primary'>|</span> {cruceSeleccionado?.Fecha?.split('T')[1]?.split('.')[0]}
                        </span>
                        <br></br>
                        Caseta:
                        <span className='ml-1 text-secondary font-weight-bolder'> {cruceSeleccionado?.Caseta}</span>
                        <br></br>
                        Importe:
                        <span className='ml-1 text-secondary font-weight-bolder'>$ {cruceSeleccionado?.Importe?.toFixed(2)}</span>
                      </div>
                      <div className="form-floating pr-2 col">
                        Matricula/Operador registrado en el cruce:
                        <span className='ml-1 text-secondary font-weight-bolder'> {cruceSeleccionado?.No_Economico}</span>
                        <br></br>
                        OT:
                        <span className='ml-1 text-secondary font-weight-bolder'> {cruceSeleccionado?.id_orden || 'Sin OT'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mt-1">
                  <div className='card-header text-center'>
                    <Tag size={18} className="me-1" />
                    <span className='text-primary font-weight-bolder ml-2'>Responsable del TAG</span>
                  </div>
                  <div className="card-body border" style={{ background: 'whitesmoke', }}>
                    {responsableTag ? (
                      <div className='form-row align-items-center'>
                        <div className="form-floating pr-2 col">
                          <User size={18} className="me-1 mr-2 text-secondary" />
                          Responsable:
                          <span className='ml-1 text-secondary font-weight-bolder'>
                            {responsableTag.id_matricula} {responsableTag.Nombres} {responsableTag.Ap_paterno} {responsableTag.Ap_materno}
                          </span>
                          <br></br>
                          Asignado desde:
                          <span className='ml-1 text-secondary font-weight-bolder'>{formatearFecha(responsableTag.Fecha_alta) || 'Sin fecha'}</span>
                          {(responsableTag.Fecha_alta && cruceSeleccionado?.Fecha && responsableTag.Fecha_alta > cruceSeleccionado.Fecha) && (
                            <small className='ml-2 text-danger font-weight-bold'>
                              ⚠ El TAG se asignó después de la fecha del cruce; verifica quién lo portaba al momento del abuso.
                            </small>
                          )}
                        </div>
                        <div className="form-floating pr-2 col">
                          {matriculasCoinciden ? (
                            <div className="alert alert-success mb-0 py-2">
                              La matrícula del cruce <b>({matriculaDelCruce})</b> coincide con el responsable del TAG.
                            </div>
                          ) : (
                            <div className="alert alert-warning mb-0 py-2">
                              <AlertTriangle size={18} className="me-1 mr-2" />
                              La matrícula del cruce <b>({matriculaDelCruce})</b> NO coincide con el responsable del TAG <b>({matriculaResponsable})</b>.
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="alert alert-warning mb-0 py-2">
                        <AlertTriangle size={18} className="me-1 mr-2" />
                        El dispositivo <b>{normalizarTag()}</b> no se encontró en el control de TAGs; no hay responsable registrado.
                      </div>
                    )}
                  </div>
                </div>

                <div className="card mt-1">
                  <div className='card-header text-center'>
                    <User size={18} className="me-1" />
                    <span className='text-primary font-weight-bolder ml-2'>Matrícula a la que se asignará el abuso</span>
                  </div>
                  <div className="card-body border" style={{ background: 'whitesmoke', }}>
                    <div className="form-check form-switch" style={{ height: '2rem' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="inCorregirMatricula"
                        checked={corregirMatricula}
                        onChange={(e) => setCorregirMatricula(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="inCorregirMatricula">
                        La matrícula del cruce no es la correcta: corregirla y asignar el abuso a otro operador.
                      </label>
                    </div>

                    {corregirMatricula && (
                      <div className='form-row align-items-center mt-2'>
                        <div className="form-floating pr-2 col-4">
                          <input
                            type="text"
                            id="inFiltroOperador"
                            className="form-control form-control-sm"
                            placeholder="Filtrar operador..."
                            autoComplete="off"
                            value={filtroOperador}
                            onChange={(e) => setFiltroOperador(e.target.value)}
                          />
                          <label htmlFor="inFiltroOperador">Filtrar por matrícula o nombre</label>
                        </div>
                        <div className="form-floating pr-2 col">
                          <select
                            className="form-select form-control"
                            id="inOperadorAbuso"
                            value={operadorSeleccionado}
                            onChange={(e) => setOperadorSeleccionado(e.target.value)}
                          >
                            <option value="">-- Selecciona al operador que cometió el abuso --</option>
                            {opcionesOperadores.map(op => (
                              <option key={op.id} value={op.id}>
                                {op.id} | {op.nombre} {op.activo === false ? '(Baja)' : ''} {String(op.id) === matriculaResponsable ? '(Responsable del TAG)' : ''}
                              </option>
                            ))}
                          </select>
                          <label className="form-label text-primary font-weight-bold" htmlFor='inOperadorAbuso'>Operador responsable del abuso</label>
                          <small className="ml-1 form-text text-muted">
                            Se actualizará la matrícula del registro en la tabla de cruces.
                          </small>
                        </div>
                      </div>
                    )}

                    {/* La matrícula tecleada no está entre los operadores activos: permitir buscarla en el histórico (incluye bajas). */}
                    {ofrecerBusquedaHistorico && (
                      <div className="alert alert-info d-flex align-items-center justify-content-between py-2 mt-2 mb-0">
                        <span>
                          La matrícula <b>{matriculaFiltro}</b> no aparece entre los operadores activos.
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary ml-2"
                          disabled={buscandoMatricula}
                          onClick={buscarMatriculaHistorico}
                        >
                          <Search size={16} className="me-1 mr-1" />
                          {buscandoMatricula ? 'Buscando...' : `Buscar ${matriculaFiltro} en todo el personal (incluye bajas)`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}

      </Modal.Body>

      <Modal.Footer>
        {paso === 1 && (
          <>
            <Button variant="primary" disabled={!cruceSeleccionado} onClick={continuarPaso2}>
              Continuar
            </Button>
            <Button variant="secondary" onClick={cerrar}>
              Cerrar
            </Button>
          </>
        )}
        {paso === 2 && (
          <>
            <Button
              variant="danger"
              disabled={guardando || loadingPaso2 || (corregirMatricula && !operadorSeleccionado)}
              onClick={marcarComoAbuso}
            >
              {guardando ? 'Guardando...' : 'Marcar como ABUSO'}
            </Button>
            <Button variant="outline-secondary" disabled={guardando} onClick={() => { setPaso(1); setCorregirMatricula(false); setOperadorSeleccionado(''); }}>
              Volver
            </Button>
            <Button variant="secondary" disabled={guardando} onClick={cerrar}>
              Cerrar
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MarcarAbusoModal;
